"use client";

/**
 * useExternalWalletBalance — Lookup balance for ANY Polygon address.
 *
 * Hits the public `/api/wallet/balance/:address` endpoint, which returns
 * USDC.e + USDC native + pUSD + POL + portfolio_value via Multicall3.
 *
 * Use this for showing balances of wallets that aren't the user's
 * primary trading wallet — Safe legacy after migration to a deposit
 * wallet, the Privy EOA, or any other observed address. The primary
 * trading wallet still goes through `usePolymarketBalanceContext`,
 * which wires the freshness/auto-wrap path on top of the same data.
 *
 * 30 s React Query cache lines up with the Redis 5 s TTL on the
 * backend — the latency cost of an extra wallet card is bounded.
 */
import { useQuery } from "@tanstack/react-query";

export interface ExternalWalletBalance {
  wallet_address: string;
  usdc: number;        // USDC.e (bridged)
  usdc_native: number; // USDC native (Circle)
  pusd: number;
  tradeable: number;   // usdc.e + pusd
  pol: number;
  portfolio_value: number; // open positions value, from Polymarket data-api
}

export function useExternalWalletBalance(
  address: string | null | undefined,
  options?: { enabled?: boolean; refetchIntervalMs?: number },
) {
  const enabled = !!address && /^0x[a-fA-F0-9]{40}$/.test(address) && options?.enabled !== false;

  return useQuery<ExternalWalletBalance | null>({
    queryKey: ["external-wallet-balance", address?.toLowerCase()],
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchInterval: options?.refetchIntervalMs ?? false,
    queryFn: async () => {
      if (!address) return null;
      const res = await fetch(`/api/wallet/balance/${address}`);
      if (!res.ok) return null;
      const data = await res.json();
      return (data?.balance as ExternalWalletBalance) || null;
    },
  });
}
